import idGen from "../../utils/idGen";
import { isStarted } from "./links";

// The shape of the stored experiment (see links.js for open and started) and
// what the form and the Save derive from it.

export const DEFAULT_QUOTA = 100;

// Who decides the variation of a user: Ares (a hash in builder-block) or an
// external service (it sends exp_variant=<value>)
export const assignmentOf = (experiment) =>
  experiment?.assignment === "external" ? "external" : "ares";

export const strategyOf = (experiment) =>
  experiment?.trafficStrategy === "custom" ? "custom" : "roundRobin";

// "" while the field is being typed: an error of the validation, not a default
export const quotaOf = (experiment) => experiment?.quota ?? DEFAULT_QUOTA;

// Custom weights count only when Ares assigns with the custom strategy
export const hasWeights = (experiment) =>
  assignmentOf(experiment) === "ares" && strategyOf(experiment) === "custom";

export const isArchived = (experiment) => Boolean(experiment?.outcome);

const LETTERS = "abcdefghijklmnopqrstuvwxyz";

// a, b ... z, aa, ab ...
function valueAt(n) {
  let value = "";
  let rest = n + 1;

  while (rest > 0) {
    rest -= 1;
    value = LETTERS[rest % 26] + value;
    rest = Math.floor(rest / 26);
  }

  return value;
}

// The next free value of a variation: a for the control, then b, c...
export function nextValue(variations = []) {
  const used = new Set(
    variations.map((variation) =>
      String(variation?.value ?? "")
        .trim()
        .toLowerCase()
    )
  );

  for (let n = 0; ; n++) {
    const value = valueAt(n);
    if (!used.has(value)) return value;
  }
}

// The control: always the first variation, its page is the default one of the
// endpoint
export const controlVariation = (endpoint, id = idGen()) => ({
  id,
  label: "Control",
  value: "a",
  blockId: endpoint?.blockId,
  control: true
});

// A new variant, with no page yet
export const newVariation = (variations = [], id = idGen()) => {
  const value = nextValue(variations);

  return { id, label: `Variant ${value.toUpperCase()}`, value };
};

// Draft / Scheduled / Running / Paused / Promoted / Closed (Ended: Running
// past its end date)
export function statusOf(experiment, now = Date.now()) {
  if (experiment?.outcome)
    return experiment.outcome.action === "promoted" ? "Promoted" : "Closed";
  if (!experiment?.running) return isStarted(experiment) ? "Paused" : "Draft";

  const { start, end } = experiment.scheduling ?? {};
  if (start && new Date(start).getTime() > now) return "Scheduled";
  if (end && new Date(end).getTime() < now) return "Ended";

  return "Running";
}

// The values of the form as the Save stores them (changed in place and
// returned). stored: the stored row, none for a new experiment.
// - startedAt is set by the first Save with Running on and never changes;
// - quota, strategy and weights only exist with Ares as the assigner;
// - the fields of the old model go (payload, winner, active);
// - archived (outcome): no reference, not running.
export function prepareExperiment(values, stored, now = new Date()) {
  values.running = Boolean(values.running);

  if (stored?.startedAt) values.startedAt = stored.startedAt;
  else if (values.running) values.startedAt = now.toISOString();
  else delete values.startedAt;

  // A new experiment (also the copy of another) has no outcome yet
  if (!stored) {
    delete values.outcome;
    delete values.archivedReference;
  }

  if (values.outcome) {
    values.archivedReference ??= stored?.reference;
    values.running = false;
    delete values.reference;
  }

  values.assignment = assignmentOf(values);
  const weights = hasWeights(values);

  if (values.assignment === "external") {
    delete values.quota;
    delete values.trafficStrategy;
  } else {
    const quota = quotaOf(values);
    values.quota = quota === "" ? quota : Number(quota);
    values.trafficStrategy = strategyOf(values);
  }

  values.variations = (values.variations ?? []).map(
    (
      { payload, winner, active, control, traffic, value, ...variation },
      index
    ) => ({
      ...variation,
      value: String(value ?? "").trim(),
      ...(index === 0 && { control: true }),
      ...(weights && { traffic: Number(traffic) || 0 })
    })
  );

  return values;
}
